import React, { ReactNode, useContext, useEffect, useState } from 'react';

import SalesOrdersContext from '@totem/components/salesOrders/list/SalesOrdersContext';
import { SalesOrderLine } from '@totem/components/salesOrders/list/types';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Params } from '@totem/types/common';
import { SALES_ORDER_ENDPOINT } from '@totem/utilities/endpoints';

interface SalesOrderLineResults {
  lines: SalesOrderLine[];
  paging: {
    totalRecords: number;
  };
}

type Props = {
  salesOrderNumber: string;
  children?: ReactNode;
};

const SalesOrderDetailLinesContainer = ({
  salesOrderNumber,
  children,
}: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const {
    buildParameters,
    filterUpdateCount,
    sortingUpdateCount,
    pagingUpdateCount,
    isReady,
  } = useContext(InputContext);

  const [loading, setLoading] = useState<boolean>(true);
  const [lines, setLines] = useState<SalesOrderLine[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [refreshData, setRefreshData] = useState<boolean>(true);

  useEffect(() => {
    if (isReady) {
      setRefreshData(true);
    }
  }, [isReady, filterUpdateCount, sortingUpdateCount, pagingUpdateCount]);

  useEffect(() => {
    if (!isReady || !refreshData) {
      return;
    }

    setLoading(true);

    const params: Params = buildParameters();

    fetch(
      `${SALES_ORDER_ENDPOINT}/${encodeURIComponent(salesOrderNumber)}/lines`,
      {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify(params),
      },
    )
      .then((res) => res.json())
      .then((result: SalesOrderLineResults) => {
        setLines(result?.lines ?? []);

        setTotalRecords(result?.paging?.totalRecords ?? 0);
      })
      .then(() => setLoading(false));

    setRefreshData(false);
  }, [refreshData, isReady, getApiToken, salesOrderNumber]);

  return (
    <SalesOrdersContext.Provider
      value={{
        loading,
        lines,
        totalRecords,
        refetch: () => setRefreshData(true),
      }}
    >
      {children}
    </SalesOrdersContext.Provider>
  );
};

export default SalesOrderDetailLinesContainer;
